import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, fileUrl } from "@/lib/api";
import { ArrowLeft, MapPin, Users, Store, MessageCircle } from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";
import SocialIcons from "@/components/SocialIcons";
import ContributorBadge from "@/components/ContributorBadge";

export default function KecamatanPage() {
  const { nama } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("members");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/kecamatan/${encodeURIComponent(nama)}`);
        setData(data);
      } catch (err) { console.error("Failed to load kecamatan:", err); }
    })();
  }, [nama]);

  if (!data) return <Layout><div className="text-bizma-subtext">Memuat...</div></Layout>;

  const members = data.members || [];
  const businesses = data.businesses || [];

  return (
    <Layout>
      <Link to="/app" className="inline-flex items-center gap-2 text-sm text-bizma-subtext mb-6" data-testid="back-to-directory">
        <ArrowLeft size={16}/> Kembali ke Direktori
      </Link>
      <div className="mb-8" data-testid="kecamatan-header">
        <div className="text-xs uppercase tracking-widest text-bizma-sage font-bold flex items-center gap-1"><MapPin size={12}/> Grup Kecamatan</div>
        <h1 className="text-3xl sm:text-4xl font-display font-bold mt-1">{nama}, Magelang</h1>
        <p className="text-bizma-subtext mt-2">{members.length} anggota • {businesses.length} bisnis di kecamatan ini.</p>
      </div>

      <div className="flex items-center gap-2 mb-6">
        <button onClick={() => setTab("members")}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === "members" ? "bg-bizma-ink text-white" : "bg-white border border-bizma-border text-bizma-ink hover:bg-bizma-muted"}`}
          data-testid="kec-tab-members"><Users size={16}/> Anggota ({members.length})</button>
        <button onClick={() => setTab("businesses")}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === "businesses" ? "bg-bizma-ink text-white" : "bg-white border border-bizma-border text-bizma-ink hover:bg-bizma-muted"}`}
          data-testid="kec-tab-businesses"><Store size={16}/> Bisnis ({businesses.length})</button>
      </div>

      {tab === "members" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6" data-testid="kec-members-grid">
          {members.map((m) => (
            <div key={m.user_id} className="felt-card overflow-hidden transition-transform hover:-translate-y-1 hover:shadow-md flex flex-col" data-testid={`kec-member-${m.user_id}`}>
              <Link to={`/app/members/${m.username || m.user_id}`} className="p-6 flex flex-col items-center text-center flex-1">
                {m.photo_path ? (
                  <img src={fileUrl(m.photo_path)} alt={m.name} className="w-24 h-24 rounded-full object-cover ring-4 ring-bizma-sage/40" />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-bizma-sage text-white grid place-items-center text-3xl font-semibold">{m.name?.charAt(0)}</div>
                )}
                <div className="mt-4 font-display font-semibold text-lg flex items-center gap-1 justify-center">
                  {m.name}
                  {m.is_verified && <VerifiedBadge size={16} />}
                </div>
                {m.username && <div className="text-xs text-bizma-subtext">@{m.username}</div>}
                {m.first_business ? (
                  <div className="mt-2 text-xs text-bizma-terracotta">Owner {m.first_business.name}</div>
                ) : (
                  <div className="mt-2 text-xs text-bizma-subtext">Belum ada bisnis</div>
                )}
                <ContributorBadge active={m.is_top_contributor} className="mt-2" />
                <SocialIcons socials={m.socials} className="mt-3" />
              </Link>
              {m.whatsapp && (
                <div className="px-6 pb-6">
                  <a href={`https://wa.me/${m.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer"
                    className="btn-wa text-sm py-2 px-4 w-full" data-testid={`kec-wa-${m.user_id}`}>
                    <MessageCircle size={16} /> WhatsApp
                  </a>
                </div>
              )}
            </div>
          ))}
          {members.length === 0 && <div className="col-span-full felt-card p-10 text-center text-bizma-subtext">Belum ada anggota di kecamatan ini.</div>}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="kec-businesses-grid">
          {businesses.map((b) => (
            <Link key={b.business_id} to={`/app/businesses/${b.business_id}`} className="felt-card overflow-hidden transition-transform hover:-translate-y-1 hover:shadow-md" data-testid={`kec-business-${b.business_id}`}>
              <div className="aspect-video bg-bizma-muted overflow-hidden">
                {b.photo_path ? <img src={fileUrl(b.photo_path)} alt={b.name} className="w-full h-full object-cover" /> :
                  <div className="w-full h-full grid place-items-center text-bizma-subtext"><Store /></div>}
              </div>
              <div className="p-5">
                <div className="text-xs uppercase tracking-widest text-bizma-sage font-bold">{b.category}</div>
                <div className="font-display font-semibold text-lg mt-1">{b.name}</div>
                <div className="text-sm text-[#4A4743] line-clamp-2 mt-1">{b.description}</div>
                <div className="mt-3 text-sm text-bizma-subtext">oleh <span className="font-medium text-bizma-ink">{b.owner_name}</span></div>
              </div>
            </Link>
          ))}
          {businesses.length === 0 && <div className="col-span-full felt-card p-10 text-center text-bizma-subtext">Belum ada bisnis di kecamatan ini.</div>}
        </div>
      )}
    </Layout>
  );
}
